import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer.tsx";
import { Stack } from "@mui/material";
import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import type { FieldConfiguration } from "../../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type { DimensionField, ReportField } from "../../../../../../../../../api/types/svTypes.ts";
import { useAppDispatch } from "../../../../../store/hooks/useAppDispatch";
import { selectColumns, selectRows, selectValues } from "../../../../../store/selectors/fieldsStateSelectors";
import { fieldsStateActions } from "../../../../../store/thunks/fieldStatesThunks";
import { isCustomColumn, isDimensionBased, isMeasure } from "../../../../../utilities/fieldsHelper.ts";
import DimensionFormattingContainer from "../../../common/fields/format/DimensionFormattingContainer.tsx";
import { canApplyFormat } from "../../../common/fields/format/dimensionFormattingHelper.tsx";
import MeasureFieldsContainer from "../../../common/fields/format/MeasureFieldsContainer.tsx";
import TextFieldContainer from "./TextFieldsContainer.tsx";

export default function FormatContainer({ readonlyMode }: { readonlyMode: boolean }) {
  const rowsAreaValues = useSelector(selectRows);
  const columnsAreaValues = useSelector(selectColumns);
  const valuesAreaValues = useSelector(selectValues);
  const dispatch = useAppDispatch();

  const fields = useMemo(() => rowsAreaValues.concat(columnsAreaValues), [rowsAreaValues, columnsAreaValues]);
  const supportingFormattingDimensions = useMemo(
    () => valuesAreaValues.filter(isDimensionBased).filter(canApplyFormat),
    [valuesAreaValues]
  );
  const supportingFormattingMeasures = useMemo(
    () => valuesAreaValues.filter((f) => isMeasure(f) || isCustomColumn(f)),
    [valuesAreaValues]
  );

  const handleUpdateConfig = useCallback(
    (field: DimensionField, changes: Partial<FieldConfiguration>) => {
      if (columnsAreaValues.indexOf(field) > -1) {
        dispatch(fieldsStateActions.updateColumnConfig({ field, changes }));
      } else if (rowsAreaValues.indexOf(field) > -1) {
        dispatch(fieldsStateActions.updateRowConfig({ field, changes }));
      }
    },
    [rowsAreaValues, columnsAreaValues, dispatch]
  );

  const handleDimensionChange = useCallback(
    (dimension: ReportField, changes: Partial<FieldConfiguration>) => {
      const field = valuesAreaValues.find((f) => f.config.guid === dimension.config.guid);
      if (isDimensionBased(field)) {
        dispatch(fieldsStateActions.updateMeasureConfig({ field, changes }));
      }
    },
    [valuesAreaValues, dispatch]
  );

  const handleMeasureChange = useCallback(
    (measure: ReportField, changes: Partial<FieldConfiguration>) => {
      dispatch(fieldsStateActions.updateMeasureConfig({ field: measure, changes }));
    },
    [dispatch]
  );

  return (
    <ScrollableFlexContainer scrollContainerSx={{ px: "1.3rem" }}>
      <Stack sx={{ gap: 1 }}>
        <TextFieldContainer fields={fields} onUpdateConfig={handleUpdateConfig} readonlyMode={readonlyMode} />
        <DimensionFormattingContainer
          dimensions={supportingFormattingDimensions}
          onUpdateConfig={handleDimensionChange}
          readonlyMode={readonlyMode}
        />
        <MeasureFieldsContainer
          measures={supportingFormattingMeasures}
          onChange={handleMeasureChange}
          readonlyMode={readonlyMode}
        />
      </Stack>
    </ScrollableFlexContainer>
  );
}
